import type { DynamicFormResponse, FieldValue } from '../../form-engine/types';
import type { FlagRow } from '../../components/flags/registry';
import type { Persona } from '../../console/_statics/persona.statics';
import type { Countries } from '../../i18n/i18n.language-config';
import type { AccountGroupResponseDto } from '../../types/account-groups/account-group.dto';
import type { Nullable } from '../../types/common/utility';
import type { ProductType } from '../../types/products/index';
import type { SubmissionCoverageDto } from './submission-coverages.types';

export enum SUBMISSION_SOURCE {
  API = 'API',
  BROKER_PORTAL = 'BROKER_PORTAL',
  EMAIL = 'EMAIL',
  // Submissions created through the chat-agent/MCP door. Extractor-sourced like
  // EMAIL, but with no real SMTP thread behind the msgId.
  OMNI = 'OMNI',
}

export enum SubmissionStatus {
  INCOMPLETE = 'INCOMPLETE',
  PROCESSED = 'PROCESSED',
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
  LOST = 'LOST',
  EXPIRED = 'EXPIRED',
  INVALID = 'INVALID',
  CARRIER_REFERRAL = 'CARRIER_REFERRAL',
  REFERRAL = 'REFERRAL',
  SCHEDULED = 'SCHEDULED',
  NON_RENEWED = 'NON_RENEWED',
  NEEDS_APP = 'NEEDS_APP',
  BIND_ORDER_RECEIVED = 'BIND_ORDER_RECEIVED',
  BOUND = 'BOUND',
  RENEWED = 'RENEWED',
  MIGRATED = 'MIGRATED',
  CANCELLED = 'CANCELLED',
  DISCARDED = 'DISCARDED',
  EXTENDED = 'EXTENDED',
}

export type DeclineReasonsDto = {
  creationAndEditDeclineReasons?: string[];
  securityQuestionDeclineReasons?: string[];
  uwDeclineReason?: string;
  riskGenerationDeclineReasons?: string[];
  hardlinkedToOtherAgency?: string;
  allDeclineReasons?: string[];
};

export type SubmissionDetailsDto = {
  /** True when the submission is older than the read-only cutoff; only a Cowbell admin can change it. */
  readOnly?: boolean;
  agencyAccount?: AgencyAccount;
  assignee: string | null;
  automation?: boolean;
  carrierReferralReasons?: string[];
  isRenewal: boolean;
  created: string;
  createdBy?: string;
  createdByAccountType?: Persona;
  dynamicFields: Record<string, unknown>;
  declineReasons?: DeclineReasonsDto;
  expiryDate: string | null;
  firmographics: SubmissionFirmographics;
  id: string;
  industryClass: number;
  isArchived: boolean;
  isExcess: boolean;
  metaData: MetaData;
  modified: string;
  msgId?: string | null;
  needBy: string | null;
  productLine: ProductLine;
  productLines: ProductLine[];
  submissionNumber: string;
  submissionSource: SUBMISSION_SOURCE;
  submissionStatus: string;
  submissionDesc?: string;
  viable: boolean;
  urgency?: string;
  submissionCoverage?: SubmissionCoverageDto;
  willReferToCarrier?: boolean;
  expiringPolicyProduct?: ProductType;
  expiringPolicyPremium?: number;
  expiringPolicyNumber?: string;
  expiringPolicyId?: string;
  uwEmail?: string;
  tamEmail?: string;
  aeEmail?: string;
  correspondenceEmail?: string;
  triagingOutcome?: SubmissionStatus;
  agencyState?: string | null;
};

export enum ConfidenceCapCategory {
  MANDATORY_FIELD_MISSING = 'MANDATORY_FIELD_MISSING',
  SECURITY_QUESTION_MISSING = 'SECURITY_QUESTION_MISSING',
  COVERAGE_OPTIONS_MISSING_PRODUCT = 'COVERAGE_OPTIONS_MISSING_PRODUCT',
}

export type ConfidenceCapReason = {
  category: ConfidenceCapCategory;
  fieldKey?: string;
  label?: string;
  detail?: string;
};

export type SubmissionDetailsWithConfidenceDto =
  Partial<SubmissionDetailsDto> & {
    scores: {
      firmographics: Record<string, number>;
      summary: Record<string, number>;
    };
    groupConfidenceScores?: {
      accountInfoConfidenceScore: number;
      detailsConfidenceScore: number;
      industryConfidenceScore: number;
      aniConfidenceScore: number;
      organizationContactConfidenceScore: number;
      securityContactConfidenceScore: number;
      submissionCoveragesConfidenceScore: number;
      dynamicFieldsConfidenceScore: number;
    };
    overallConfidenceScore?: number;
    confidenceCapped?: boolean;
    confidenceCapReasons?: ConfidenceCapReason[];
  };

export type SubmissionsDetailsResponseDto = {
  firmographics: any;
  summary: Partial<SubmissionDetailsDto>;
  submissionCoverage?: SubmissionCoverageDto; // Pretty sure this is not right but it stops Tommy's view from complaining for tonight
  groupConfidenceScores?: {
    accountInfoConfidenceScore: number;
    detailsConfidenceScore: number;
    industryConfidenceScore: number;
    aniConfidenceScore: number;
    organizationContactConfidenceScore: number;
    securityContactConfidenceScore: number;
    submissionCoveragesConfidenceScore: number;
    dynamicFieldsConfidenceScore: number;
  };
  overallConfidenceScore?: number;
  confidenceCapped?: boolean;
  confidenceCapReasons?: ConfidenceCapReason[];
};

export type SubmissionClaimsStatusDto = {
  hasClaims?: boolean | null;
  claimStatus?: string | null;
};

export type SubmissionFirmographics = {
  additionalNamedInsured: string[];
  address: USAddress;
  changeInScope: boolean | null;
  changeInScopeDetail?: string;
  dbaOrTradestyle: string | null;
  description: string;
  domainName: string;
  domesticRevenue: number;
  dunsNumber: string;
  ein: string | null;
  entityType: string;
  foreignRevenue: number | null;
  franchiseSystemConnectivity: boolean | null;
  isFranchise: boolean;
  mailingAddress: USAddress;
  naicsCode: string;
  name: string;
  noOfEmployeesAll: number;
  numberOfPersonalRecords: number | null;
  otherDomains: string[];
  ownershipType: string;
  phoneNumber: string;
  physicalAddress: USAddress;
  policyContact: PolicyContact | null;

  policyContactEmailAddress?: string;
  policyContactFirstName?: string;
  policyContactLastName?: string;
  policyContactPhoneNumber?: string;
  securityContactFirstName?: string;
  securityContactLastName?: string;
  securityContactEmailAddress?: string;
  securityContactPhoneNumber?: string;
  securityContactTitle?: string;
  securityContactCompanyName?: string;

  primaryIndustry: string;
  primaryNaicsPercentage: number;
  secondaryIndustry: string;
  secondNaicsCode?: number;
  secondPrimaryIndustry?: string;
  secondSecondaryIndustry?: string;
  securityContact: SecurityContact | null;
  sicCode?: string;
  sicPrimaryDesc?: string;
  sicSecondaryDesc?: string;
  totalRevenue: number;
  url: string | null;
  yearEstablished: number;
  industryClass: number;
};

type PolicyContact = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
};

type SecurityContact = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  title: string;
  company: string;
};

export type SubmissionTableRow = {
  /** True when the submission is older than the read-only cutoff; only a Cowbell admin can change it. */
  readOnly?: boolean;
  currentStage?: string | null;
  currentStageState?: string | null;
  /**
   * Canonical display flags emitted by the backend search response
   * (FlagEnricher). Rendered generically via `renderFlags`. Absent for
   * external personas (never enriched).
   */
  flags?: FlagRow['flags'];
  /**
   * The submission's own account group (pool or program), if any. Independent
   * of `flags` — this is used for the Program/Pool filter columns, not the badge.
   */
  accountGroup?: AccountGroupResponseDto;
  agencyAccount?: AgencyAccount;
  aeEmail?: string;
  uwEmail?: string;
  tamEmail?: string;
  agentId: string;
  assignee: string;
  created: string;
  createdBy?: string;
  createdByAccountType?: Persona;
  declineReasons: DeclineReasonsDto | null;
  firmographics: Firmographics;
  id: string;
  metaData: MetaData;
  modified: string;
  msgId: string | null;
  needBy: string | null;
  expiryDate: string;
  /**
   * @deprecated Use `productLines` instead.
   */
  productLine: Nullable<ProductLine>;
  productLines: ProductLine[];
  submissionNumber: string;
  submissionSource: SUBMISSION_SOURCE;
  submissionStatus: SubmissionStatus;
  isExcess?: boolean;
  /** Set on the search response; the new-business listing skips the archived ones. */
  isArchived?: boolean;
  crimeLimit?: number | null;
  viable: boolean;
  expiringPolicyId?: string;
  expiringPolicyProduct?: string;
  expiringPolicyNumber?: string;
  expiringPolicyPremium?: number;
  expiringPolicyLimit?: number | null;
  renewalPremium?: number | null;
  agencyState?: string | null;
  lostReason?: string | null;
  lostReasonDetails?: {
    competitor?: string;
    notes?: string;
    premium?: number;
  } | null;

  /**
   * @deprecated for productLine
   */
  lineOfBusiness: string;
  carrierReferralReasons?: string[];

  overallConfidenceScore?: number;
  accountInfoConfidenceScore?: number;
  detailsConfidenceScore?: number;
  industryConfidenceScore?: number;
  aniConfidenceScore?: number;
  organizationContactConfidenceScore?: number;
  securityContactConfidenceScore?: number;
  submissionCoveragesConfidenceScore?: number;
  dynamicFieldsConfidenceScore?: number;
  claimStatus?: string;
};

export type LicensedAgentInfo = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  agentLicenseId?: string;
  agentNPN?: string;
  agentLicenseNumber?: string;
};

export type ServiceAgentInfo = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
};

type AgencyAccount = {
  created: string;
  modified: string;

  agencyName: string;
  agencyId: string;
  accountId: string;
  optOutRetreads: boolean;
  agent: Agent;
  licensedAgent?: LicensedAgentInfo;
  serviceAgent?: ServiceAgentInfo;
};

type Agent = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  team: { teamId: string; teamName: string };
  fullName?: string;
};

type USAddress = {
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  country: Countries;
  state: string;
  zipCode: string;
};

type Firmographics = Record<string, unknown> & {
  address: USAddress;
  physicalAddress: USAddress;
};

type ProductLine = {
  created: string;
  id: string;
  key: string;
  modified: string;
  name: string;
  prefix: string;
};

export type CreateSubmissionPayload = {
  cbid?: string;
  firmographics: Firmographics;
  lineOfBusiness?: string;

  agency?: {
    agencyId?: string;
    agencyName?: string;

    agent: {
      firstName?: string;
      lastName?: string;
      emailAddress?: string;
      phoneNumber?: string;
      teams?: Record<string, unknown>[];
    };
  };
};

export interface ISubmissionMetricsResponse {
  totalSubmissions: number;
  renewals: number;
  totalQuotedPremium: number;
  avgQuotedPremium: number;
  declined: number;
  lost: number;
}

/**
 * GET /v1/submission/{id}/questions: one form across the coverage options' products, in the dynamic-form shape the
 * forms engine draws, with the answers to unasked fields its conditions read and what its inputs look things up by.
 */
export type SubmissionMergedQuestionsDto = DynamicFormResponse & {
  readonly version: number;
  readonly products: ReadonlyArray<{ productType: string; name: string }>;
  readonly otherAnswers: Record<string, FieldValue>;
  readonly metadata: Record<string, unknown>;
};

export type SubmissionEligibleProductsResponse = {
  eligibleProducts: EligibleProduct[];
  omissionReasons?: string[];
  selectedRenewalProduct?: ProductType;
};

export type EligibleProduct = {
  productType: ProductType;
  productLine: ProductLine;
};

export type DeclineSubmissionPayload = {
  declinationReason: string;
  declinationReasonDescription: string;
};

type MetaData = {
  systemDefaults?: SystemDefaults;
  agencyMatch?: AgencyMatch;
  cbids?: string[];
  name?: string | null;
  state?: string | null;
  msgId?: string | null;
};

type SystemDefaults = {
  additionalNamedInsuredDefaulted: boolean;
  changeInScopeDefaulted: boolean;
  domesticRevenueDefaulted: boolean;
  entityTypeDefaulted: boolean;
  isFranchiseDefaulted: boolean;
  naicsCodeDefaulted: boolean;
  noOfEmployeesAllDefaulted: boolean;
  numberOfPersonalRecordsDefaulted: boolean;
  ownershipTypeDefaulted: boolean;
  securityContactDefaulted: boolean;
  yearEstablishedDefaulted: boolean;
};

type AgencyMatch = {
  agencyId: string | null;
  agencyName: string | null;
  agentId: string | null;
  agentFirstName: string | null;
  agentLastName: string | null;
  agentEmail: string | null;
  agentPhone: string | null;
  teamId: string | null;
  teamName: string | null;
};

export type LicensedAgentUpdate = {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  agentLicenseId?: string;
};

export type ServiceAgentUpdate = {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phoneNumber?: string;
};

export type UpdateAgencyDto = {
  agencyId: string;
  agencyName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
  teamId: string;
  teamName: string;
  licensedAgent?: LicensedAgentUpdate | null;
  serviceAgent?: ServiceAgentUpdate | null;
};
